import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../config/api';
import { detectTenantFromHost, classifyHost, getPlatformDomains } from '../lib/tenantDetection';
import { isAuthFailure, isTokenExpired, shouldRefreshToken, loginPathForSession, rememberPortal } from '../lib/session';
import { SIGNED_OUT_KEY } from '../components/security/AwayGuard';
import { ELSEWHERE_REASON } from '../hooks/useAwayGuard';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('lms_token'));
  const [loading, setLoading] = useState(true);
  const [tenantId, setTenantId] = useState(localStorage.getItem('lms_tenant_id'));
  // What this user may reach: null for a plain built-in role, otherwise the
  // narrowed module list from the role their tenant gave them.
  const [access, setAccess] = useState(null);
  const [tenantInfo, setTenantInfo] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('lms_tenant_info') || 'null');
      return stored && typeof stored === 'object' && stored.subdomain ? stored : null;
    } catch {
      return null;
    }
  });
  const [hostTenantChecked, setHostTenantChecked] = useState(false);

  // Detect tenant from current hostname (subdomain or custom domain)
  useEffect(() => {
    const detect = async () => {
      const platform = await getPlatformDomains();
      const cls = classifyHost(window.location.hostname, platform.base_domains);
      // Drop any host-derived tenant that doesn't belong to THIS host (stale or corrupt record)
      const dropStaleHostTenant = () => {
        setTenantInfo((prev) => {
          if (prev?.source && !(prev.subdomain && (
            (cls.kind === 'subdomain' && prev.subdomain === cls.value) ||
            (cls.kind === 'custom_domain' && prev.custom_domain === cls.value)
          ))) {
            localStorage.removeItem('lms_tenant_info');
            return null;
          }
          return prev;
        });
      };
      if (cls.kind === 'platform' || cls.kind === 'none') {
        dropStaleHostTenant();
        setHostTenantChecked(true);
        return;
      }
      // Short-circuit if we already have matching tenant cached
      let parsed = null;
      try { parsed = JSON.parse(localStorage.getItem('lms_tenant_info') || 'null'); } catch { parsed = null; }
      if (parsed && parsed.subdomain && (
        (cls.kind === 'subdomain' && parsed.subdomain === cls.value) ||
        (cls.kind === 'custom_domain' && parsed.custom_domain === cls.value)
      )) {
        setHostTenantChecked(true);
        return;
      }
      const detected = await detectTenantFromHost(window.location.hostname);
      if (!detected) dropStaleHostTenant();
      if (detected) {
        const info = {
          id: detected.id,
          name: detected.name,
          subdomain: detected.subdomain,
          base_domain: detected.base_domain || null,
          host: detected.host || null,
          custom_domain: detected.custom_domain || null,
          source: cls.kind  // 'subdomain' | 'custom_domain'
        };
        localStorage.setItem('lms_tenant_info', JSON.stringify(info));
        localStorage.setItem('lms_tenant_id', detected.id);
        setTenantInfo(info);
        setTenantId(detected.id);
      }
      setHostTenantChecked(true);
    };
    detect();
  }, []);

  /*
   * ONE axios instance for the whole app, created once.
   *
   * It used to be re-created on every render, which meant the interceptors below could
   * never be attached reliably and every hook taking `api` as a dependency re-ran on each
   * render. The token is read from a ref at request time, so a single long-lived instance
   * always sends the current one.
   */
  const tokenRef = useRef(token);
  useEffect(() => { tokenRef.current = token; }, [token]);

  // Set once the session has ended, so a page full of parallel requests reports it once.
  const sessionEndedRef = useRef(false);
  const endSessionRef = useRef(null);

  const clearSession = useCallback(() => {
    localStorage.removeItem('lms_token');
    localStorage.removeItem('lms_tenant_id');
    localStorage.removeItem('lms_tenant_subdomain');
    localStorage.removeItem('lms_tenant_info');
    localStorage.removeItem('lms_user');
    tokenRef.current = null;
    setToken(null);
    setUser(null);
    setAccess(null);
    setTenantId(null);
    setTenantInfo(null);
  }, []);


  const api = useMemo(() => {
    const instance = axios.create({ baseURL: API_URL });

    instance.interceptors.request.use((config) => {
      const current = tokenRef.current;
      if (current) config.headers.Authorization = `Bearer ${current}`;
      return config;
    });

    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        // An expired or rejected token used to surface as "Failed to load courses" on
        // whichever screen happened to ask first, leaving the user inside a dead shell.
        // End the session once, say so plainly, and send them back to sign in.
        if (isAuthFailure(error) && !error.config?.skipAuthRedirect) {
          endSessionRef.current?.(error.response?.data?.detail);
        }
        return Promise.reject(error);
      }
    );

    return instance;
  }, []);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    // A token left over from yesterday is dead on arrival. Drop it before any request
    // goes out: the user is simply signed out, which beats a cascade of failing panels.
    if (isTokenExpired(token)) {
      clearSession();
      setLoading(false);
      return;
    }
    try {
      const response = await api.get('/auth/me');
      const userData = response.data;
      setUser(userData);

      // What this user may reach. Asked for rather than guessed from the role name, so
      // the menu and the server cannot drift apart.
      try {
        const accessRes = await api.get('/me/access', { skipAuthRedirect: true });
        setAccess(accessRes.data);
      } catch {
        setAccess(null);   // an older server, or a blip: fall back to the plain role
      }
      // Kept so the app can still open offline: the token is valid, the server is simply
      // out of reach, and bouncing someone to a sign-in page on a train is not an app.
      try { localStorage.setItem('lms_user', JSON.stringify(userData)); } catch { /* private mode */ }
      
      // If user has a tenant_id but tenantInfo is missing, fetch it
      if (userData.tenant_id && !tenantInfo) {
        try {
          const tenantResponse = await api.get('/tenant/me');
          const info = {
            id: tenantResponse.data.id,
            name: tenantResponse.data.name,
            subdomain: tenantResponse.data.subdomain,
            base_domain: tenantResponse.data.base_domain || null,
            host: tenantResponse.data.host || null,
            custom_domain: tenantResponse.data.custom_domain
          };
          localStorage.setItem('lms_tenant_info', JSON.stringify(info));
          setTenantInfo(info);
        } catch (tenantErr) {
          console.warn('Could not fetch tenant info:', tenantErr);
        }
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // A 401 is handled by the interceptor. Anything else (API restarting, network
      // blip) must NOT throw the session away — the token is still good.
      if (isAuthFailure(error)) {
        clearSession();
      } else {
        // Unreachable, not unauthorised: carry on with the profile we already had, so an
        // installed app opens to the shell rather than a sign-in page.
        try {
          const cached = JSON.parse(localStorage.getItem('lms_user') || 'null');
          if (cached && cached.id) setUser(cached);
        } catch { /* nothing cached */ }
      }
    } finally {
      setLoading(false);
    }
  }, [token, api, clearSession, tenantInfo]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  /*
   * Keep a working session alive.
   *
   * The token has a fixed life and no sliding window, so without this anyone signed in
   * yesterday is thrown out mid-task today. While the tab is open we swap it for a fresh
   * one once it is inside the refresh window — and again whenever the tab regains focus,
   * which is when a laptop coming out of sleep finds its token nearly dead.
   */
  useEffect(() => {
    if (!token || !user) return undefined;
    let cancelled = false;

    const renew = async () => {
      if (cancelled || !tokenRef.current || !shouldRefreshToken(tokenRef.current)) return;
      try {
        const res = await api.post('/auth/refresh', null, { skipAuthRedirect: true });
        const fresh = res.data?.access_token;
        if (fresh && !cancelled) {
          localStorage.setItem('lms_token', fresh);
          tokenRef.current = fresh;
          setToken(fresh);
        }
      } catch {
        // Leave it. If the token really is finished the next request ends the session.
      }
    };

    renew();
    // Check every 15 minutes; the window is hours wide so this is not a busy loop.
    const timer = window.setInterval(renew, 15 * 60 * 1000);
    const onFocus = () => renew();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [token, user, api]);

  const login = async (email, password, tenant_id = null, tenantData = null) => {
    const response = await api.post('/auth/login', { email, password, tenant_id });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('lms_token', access_token);
    if (userData.tenant_id) {
      localStorage.setItem('lms_tenant_id', userData.tenant_id);
      setTenantId(userData.tenant_id);
    }
    
    // Store tenant info if provided (subdomain, custom_domain)
    if (tenantData) {
      // Survives a cleared session, so an expiry sends them back to THIS portal.
      if (userData.role !== 'super_admin') rememberPortal(tenantData.subdomain);
      const info = {
        id: tenantData.id,
        name: tenantData.name,
        subdomain: tenantData.subdomain,
        base_domain: tenantData.base_domain || null,
        host: tenantData.host || null,
        custom_domain: tenantData.custom_domain
      };
      localStorage.setItem('lms_tenant_info', JSON.stringify(info));
      setTenantInfo(info);
    }
    
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await api.post('/auth/register', userData);
    const { access_token, user: newUser } = response.data;
    
    localStorage.setItem('lms_token', access_token);
    if (newUser.tenant_id) {
      localStorage.setItem('lms_tenant_id', newUser.tenant_id);
      setTenantId(newUser.tenant_id);
    }
    
    setToken(access_token);
    setUser(newUser);
    return newUser;
  };

  const logout = useCallback(() => {
    sessionEndedRef.current = false;
    // Tell the server this device is done. Dropping the token locally used to be
    // enough; now that a tenant can cap how many devices a person uses at once, a
    // session nobody ended would keep holding one of those slots until it expired.
    try {
      const current = tokenRef.current;
      if (current) {
        api.post('/auth/logout', null, { skipAuthRedirect: true }).catch(() => {});
      }
    } catch { /* never block signing out on this */ }
    clearSession();
  }, [clearSession, api]);

  /**
   * The session is no longer usable. Say so once, keep where the user came in through,
   * and send them to the right sign-in page — tenant users back to their own portal.
   */
  const endSession = useCallback((detail) => {
    if (sessionEndedRef.current) return;
    sessionEndedRef.current = true;

    const subdomain = localStorage.getItem('lms_tenant_subdomain')
      || tenantInfo?.subdomain
      || (() => { try { return JSON.parse(localStorage.getItem('lms_tenant_info') || 'null')?.subdomain; } catch { return null; } })();
    const target = loginPathForSession({ role: user?.role, subdomain });

    clearSession();

    // "Session ended" is the server saying this device was signed out from somewhere
    // else — another sign-in took its slot, the user cleared their devices, or an admin
    // did. Leave a reason behind so the sign-in page can explain it rather than
    // claiming the session expired, which it did not.
    if (/session ended/i.test(String(detail || ''))) {
      try {
        sessionStorage.setItem(SIGNED_OUT_KEY, JSON.stringify({
          reason: ELSEWHERE_REASON, at: Date.now(),
        }));
      } catch { /* private mode */ }
    }

    const expired = !detail || /expired/i.test(String(detail));
    toast.error(expired ? 'Your session has expired — please sign in again.' : String(detail));

    // ProtectedRoute moves to the sign-in page on its own once the session is cleared.
    // Racing it with a hard redirect mounted that page TWICE, and the first mount ate
    // the reason above — so the user arrived with no explanation. Give the router a
    // moment and only force it if nothing happened, the same way AwayGuard does.
    if (typeof window !== 'undefined') {
      window.setTimeout(() => {
        if (!window.location.pathname.endsWith('/login')) window.location.href = target;
      }, 600);
    }
  }, [clearSession, tenantInfo, tenantId, user]);

  useEffect(() => { endSessionRef.current = endSession; }, [endSession]);

  const loginWithToken = async (providedToken) => {
    try {
      // Set token temporarily to make the API call
      const tempApi = axios.create({
        baseURL: API_URL,
        headers: { Authorization: `Bearer ${providedToken}` }
      });
      
      const response = await tempApi.get('/auth/me');
      const userData = response.data;
      
      // Store the token and user data
      localStorage.setItem('lms_token', providedToken);
      if (userData.tenant_id) {
        localStorage.setItem('lms_tenant_id', userData.tenant_id);
        setTenantId(userData.tenant_id);
      }
      
      setToken(providedToken);
      setUser(userData);
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('Token login failed:', error);
      return { success: false, error: error.response?.data?.detail || 'Invalid token' };
    }
  };

  const value = {
    user,
    setUser,
    token,
    loading,
    tenantId,
    setTenantId,
    tenantInfo,
    setTenantInfo,
    access,
    hostTenantChecked,
    login,
    loginWithToken,
    register,
    logout,
    api,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === 'super_admin',
    isTenantAdmin: user?.role === 'tenant_admin',
    isInstructor: user?.role === 'instructor',
    isLearner: user?.role === 'learner'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
